// GMOあおぞらネット銀行の入出金明細CSVをZeniの取引インポート形式に変換する。
//
// 銀行のWeb（入出金明細 > CSVダウンロード）から取得できるCSVは Shift-JIS で、次の列を持つ:
//   日付, 摘要, 入金金額, 出金金額, 残高, メモ
// ドコモSMTBネット銀行と違い、日付が区切り無しの "YYYYMMDD"、入金列が出金列より前に来る。
// 出金額のある行を支出、入金額のある行を収入として取り込む。
// 残高列は口座残高の再計算には使わない（取り込んだ取引の差分で口座残高が更新されるため、
// 古い期間のCSVを取り込んだときに残高が巻き戻るのを避ける）。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type GmoAozoraBankCategoryRef = CsvCategoryRef
export type GmoAozoraBankImportRow = CsvImportRow
export type GmoAozoraBankParseResult = CsvParseResult

// 店名キーワードより先に判定する銀行固有の摘要。
// 税金・公金の Pay-easy 払いは摘要が「PE 立川税務署 26074000093」のような形になる。
// 「ＡＴＭ　セブン銀行」が店名表の「セブン」＝食費に誤判定されるのもここで防ぐ。
const BANK_EXPENSE_OVERRIDE: ReadonlyArray<readonly [string, string]> = [
  ['ＡＴＭ', 'other'], ['ATM', 'other'], ['カード引出', 'other'], ['引出', 'other'],
  ['振込手数料', 'other'], ['手数料', 'other'],
  ['税務署', 'other'], ['国税', 'other'], ['地方税', 'other'], ['住民税', 'other'],
  ['所得税', 'other'], ['事業税', 'other'], ['税金', 'other'],
  ['法務省', 'other'], ['年金', 'other'], ['社会保険料', 'other'], ['国庫金', 'other'],
  // 他行への振込は摘要が相手銀行名になる。店名表に載る名前を含む銀行（ドコモＳＭＴＢ→通信費、
  // イオン銀行→食費 など）に引っ張られないよう、銀行名は先に「その他」で確定させる。
  ['銀行', 'other'], ['ドコモSMTB', 'other'], ['SMTBネツト', 'other'], ['スミシンエスビ', 'other'],
]

// 入金行の摘要から収入カテゴリを推定する。一致しなければ「その他収入」へ。
const BANK_INCOME_CATEGORY: ReadonlyArray<readonly [string, string]> = [
  ['給与', 'salary'], ['給料', 'salary'], ['キユウヨ', 'salary'], ['キュウヨ', 'salary'],
  ['賞与', 'salary'], ['ボーナス', 'salary'],
  ['利息', 'income_other'], ['配当', 'income_other'], ['還付', 'income_other'], ['返金', 'income_other'],
]

interface ColumnMap {
  date: number
  desc: number
  deposit: number
  withdrawal: number
  note: number
}

// ヘッダー行から各列の位置を特定する。見つからない列は既定の並び順にフォールバックする。
function locateColumns(header: string[]): ColumnMap {
  const find = (keywords: string[], fallback: number) => {
    const idx = header.findIndex((h) => keywords.some((k) => h.includes(k)))
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find(['日付', '取引日'], 0),
    desc: find(['摘要', '内容', 'お取引内容'], 1),
    deposit: find(['入金'], 2),
    withdrawal: find(['出金'], 3),
    note: find(['メモ'], 5),
  }
}

// 口座名やダウンロード日時が先頭に付くCSVもあり得るため、ヘッダー行を先頭数行から探す。
function findHeaderIndex(lines: string[]): number {
  const limit = Math.min(lines.length, 10)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i])
    const hasDate = cells.some((c) => c.includes('日付') || c.includes('取引日'))
    const hasAmount = cells.some((c) => c.includes('出金') || c.includes('入金'))
    if (hasDate && hasAmount) return i
  }
  return 0
}

// 摘要は「振込  サザビ－（ド」のように種別と相手先の間が半角スペース2個になることがある。
// 重複排除キーに摘要が含まれるので、取り込みごとにぶれない形へ揃えてから使う。
function normalizeDescription(raw: string | undefined): string {
  return (raw ?? '').replace(/[ \t]+/g, ' ').trim()
}

// メモ列は値が無いとき "-" が入ることがある。
function normalizeNote(raw: string | undefined): string | undefined {
  const note = (raw ?? '').trim()
  if (note === '' || note === '-' || note === '－' || note === 'ー') return undefined
  return note
}

export function parseGmoAozoraBankCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<GmoAozoraBankCategoryRef>,
): GmoAozoraBankParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const headerIndex = findHeaderIndex(lines)
  const col = locateColumns(splitCsvLine(lines[headerIndex]))

  const { expenseIds, incomeIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)
  const incomeSet = new Set(incomeIds)

  const rows: GmoAozoraBankImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(headerIndex + 1)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')
    const deposit = parseAmount(cols[col.deposit] ?? '')
    const withdrawal = parseAmount(cols[col.withdrawal] ?? '')

    // 出金・入金のどちらかに正の金額がある行だけを取引として扱う
    const isIncome = Number.isFinite(deposit) && deposit > 0
    const amount = isIncome ? deposit : withdrawal
    if (!date || !Number.isFinite(amount) || amount <= 0) {
      ignored++
      continue
    }

    const description = normalizeDescription(cols[col.desc]) || 'GMOあおぞらネット銀行'
    const category = isIncome
      ? matchKeywordCategory(description, BANK_INCOME_CATEGORY, incomeSet) ?? fallbackIncome
      : matchKeywordCategory(description, BANK_EXPENSE_OVERRIDE, expenseSet)
        ?? matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet)
        ?? fallbackExpense

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category,
      description,
      amount,
      accountId,
      currency: 'JPY',
      note: normalizeNote(cols[col.note]),
    })
  }

  return { rows, parsed: rows.length, ignored }
}
